import React, { useState, useEffect } from 'react';
import { FileText, Mail, Phone, Calendar, Users, Building2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuotesManager = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadQuotes = async () => {
      try {
        const response = await adminApi.get('/api/admin/quotes');
        setQuotes(response.data);
      } catch (error) {
        toast({ title: "Erreur", description: "Impossible de charger les devis", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    loadQuotes();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400 text-lg">Chargement des devis...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" data-testid="quotes-title">Demandes de Devis</h1>
        <p className="text-gray-400">{quotes.length} demande(s) de devis</p>
      </div>

      {quotes.length === 0 ? (
        <div className="text-center py-20 bg-gray-900 rounded-lg border border-gray-800">
          <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Aucune demande de devis</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-gray-900 rounded-lg p-6 border border-gray-800" data-testid={`quote-item-${quote.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold text-lg">{quote.name}</h3>
                  <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
                    quote.status === 'new' ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-300'
                  }`}>
                    {quote.status === 'new' ? 'Nouveau' : quote.status}
                  </span>
                </div>
                <span className="text-gray-500 text-sm">
                  {new Date(quote.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm truncate">{quote.email}</span>
                </div>
                {quote.phone && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{quote.phone}</span>
                  </div>
                )}
                {quote.guestCount && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Users className="w-4 h-4" />
                    <span className="text-sm">{quote.guestCount} invités</span>
                  </div>
                )}
                {quote.eventType && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{quote.eventType}</span>
                  </div>
                )}
                {quote.company && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Building2 className="w-4 h-4" />
                    <span className="text-sm">{quote.company}</span>
                  </div>
                )}
                {quote.eventDate && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{quote.eventDate}</span>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-800/50 rounded-lg p-4">
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{quote.message}</p>
              </div>

              {(quote.markingText || quote.logoUrl) && (
                <div className="mt-4 flex items-center gap-4 bg-purple-900/20 border border-purple-500/30 rounded-lg p-4" data-testid="quote-marking-admin">
                  {quote.logoUrl && (
                    <a href={quote.logoUrl} target="_blank" rel="noreferrer">
                      <img src={quote.logoUrl} alt="logo client" className="w-14 h-14 object-contain bg-white/90 rounded p-1" />
                    </a>
                  )}
                  <div>
                    <p className="text-purple-300 text-xs uppercase tracking-wide mb-1">Marquage demandé</p>
                    {quote.markingText && <p className="text-white text-sm">« {quote.markingText} »</p>}
                    {quote.logoUrl && <p className="text-gray-400 text-xs mt-0.5">Logo fourni par le client</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuotesManager;
